import { FC, useCallback, useMemo } from "react";

import { useFullUserProfileContext, UserEntityModalType } from "Bands/contexts";
import { copy } from "shared/assets";
import { IdCard, Plus } from "shared/assets/icons";
import { Button, Card, Loader, Message, DrivingPermitTeaser } from "shared/components";
import { Spacer } from "shared/layout";
import { GridContainer, Icon } from "shared/styles";
import { ITheme } from "shared/types";
import { useFetchingUserDocuments } from "Bands/utils";

const permitCopy = copy.userDrivingPermit;

export const DrivingPermits: FC = () => {
  const {
    drivingPermits,
    setShowCreateModal,
    setShowEditModal,
    setShowDeleteModal,
    getItemActions,
  } = useFullUserProfileContext();

  const getActions = useCallback(
    (id: string) =>
      getItemActions({
        handleEdit: () =>
          setShowEditModal({
            id: id,
            type: UserEntityModalType.drivingPermit,
          }),
        handleDelete: () =>
          setShowDeleteModal({
            id: id,
            type: UserEntityModalType.drivingPermit,
          }),
      }),
    [getItemActions, setShowDeleteModal, setShowEditModal]
  );

  const { fetchingDrivingPermits } = useFetchingUserDocuments();

  const content = useMemo(() => {
    if (fetchingDrivingPermits) {
      return <Loader />;
    }

    if (!drivingPermits?.length) {
      return <Message message={permitCopy.messages.empty} />;
    }

    return (
      <GridContainer>
        {drivingPermits?.map(permit => (
          <DrivingPermitTeaser key={permit.id} {...permit} actions={getActions(permit.id)} />
        ))}
      </GridContainer>
    );
  }, [drivingPermits, fetchingDrivingPermits, getActions]);

  return (
    <Card icon={IdCard} title={permitCopy.title}>
      <Spacer size="s">
        {content}
        <Button
          onClick={() => setShowCreateModal(UserEntityModalType.drivingPermit)}
          data-testid="add_driving_permit_button"
          theme={ITheme.secondary}
        >
          <Icon as={Plus} />
          <span>{permitCopy.buttons.create}</span>
        </Button>
      </Spacer>
    </Card>
  );
};
